/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable unused-imports/no-unused-vars */
import NutrientViewer from '@nutrient-sdk/viewer';
import { AnnotationTypeEnum } from './types';

// Date format configurations
interface DateFormatConfig {
  script: string;
  display: string;
  placeholder: string;
  maxLength: number;
}

const getDateFormat = (formatType?: string): DateFormatConfig => {
  const formats: Record<string, DateFormatConfig> = {
    US: {
      script: 'mm/dd/yyyy',
      display: 'MM/DD/YYYY',
      placeholder: 'MM/DD/YYYY',
      maxLength: 10,
    },
    EU: {
      script: 'dd/mm/yyyy',
      display: 'DD/MM/YYYY',
      placeholder: 'DD/MM/YYYY',
      maxLength: 10,
    },
    ISO: {
      script: 'yyyy-mm-dd',
      display: 'YYYY-MM-DD',
      placeholder: 'YYYY-MM-DD',
      maxLength: 10,
    },
  };

  // Default to US format if no type specified or type not found
  return formats[formatType?.toUpperCase() || 'US'] || formats['US'];
};

const handleDrop = async (
  e: any,
  inst: any,
  PSPDFKit: typeof NutrientViewer,
  currSigneeRef: any,
  currUserRef: any,
  onPageIndexRef: any,
) => {
  e.preventDefault();
  e.stopPropagation();
  const dataArray = e.dataTransfer.getData('text').split('%');

  const [name, email, InstantId, annotationType, liveFormType, isPlaceholder] =
    dataArray;

  const instantId = PSPDFKit.generateInstantId();
  const signee = currSigneeRef.current;
  const user = currUserRef.current;
  const pageIndex = onPageIndexRef.current;
  let rectWidth = 120;
  let rectHeight = 40;
  switch (annotationType) {
    case AnnotationTypeEnum.INITIAL:
      rectWidth = 80;
      rectHeight = 52;
      break;

    case AnnotationTypeEnum.SIGNATURE:
      rectWidth = 120;
      rectHeight = 60;
      break;

    case AnnotationTypeEnum.DS:
      rectWidth = 250;
      rectHeight = 100;
      break;

    case AnnotationTypeEnum.RadioButton:
      rectWidth = 24;
      rectHeight = 24;
      break;

    case AnnotationTypeEnum.CheckBox:
      rectWidth = 24;
      rectHeight = 24;
      break;

    case AnnotationTypeEnum.TextField:
    case AnnotationTypeEnum.SIGNATURE_WITH_DATE_SIGNED:
      rectWidth = 120;
      rectHeight = 40;
      break;

    default:
      break;
  }
  const clientRect = new PSPDFKit.Geometry.Rect({
    left: e.clientX - rectWidth / 2,
    top: e.clientY - rectHeight / 2,
    height: rectHeight,
    width: rectWidth,
  });
  const pageRect = inst.transformContentClientToPageSpace(
    clientRect,
    pageIndex,
  ) as any;
  if (
    annotationType === AnnotationTypeEnum.SIGNATURE ||
    annotationType === AnnotationTypeEnum.INITIAL
  ) {
    let widget;
    const customData = {
      createdBy: user.id,
      type: annotationType,
      isInitial: annotationType === AnnotationTypeEnum.INITIAL,
      isPlaceholder: Boolean(Number(isPlaceholder)),
    };
    widget = new PSPDFKit.Annotations.WidgetAnnotation({
      boundingBox: pageRect,
      formFieldName: instantId,
      id: instantId,
      pageIndex,
      name: instantId,
      subject: liveFormType,
      customData: customData,
    });
    if (liveFormType === 'multi') {
      widget = widget.set('customData', {
        ...widget.customData,
        signerID: signee.id,
        name: signee.first_name + ' ' + signee.last_name,
      });
    }
    const formField = new PSPDFKit.FormFields.SignatureFormField({
      annotationIds: PSPDFKit.Immutable.List([widget.id]),
      name: instantId,
      id: instantId,
      readOnly: signee.id !== user.id,
    });
    await inst.create([widget, formField]);
  } else if (annotationType === AnnotationTypeEnum.RadioButton) {
    const radioWidget1 = new PSPDFKit.Annotations.WidgetAnnotation({
      id: instantId,
      pageIndex: pageIndex,
      formFieldName: instantId,
      boundingBox: pageRect,
      customData: {
        createdBy: user.id,
        type: annotationType,
      },
    });
    const formField = new PSPDFKit.FormFields.RadioButtonFormField({
      id: instantId,
      name: instantId,
      annotationIds: PSPDFKit.Immutable.List.of(radioWidget1.id),
      options: PSPDFKit.Immutable.List.of(
        new PSPDFKit.FormOption({
          label: 'Option 1',
          value: 'Value 1',
        }),
      ),
      defaultValue: '1',
    });
    await inst.create([radioWidget1, formField]);
  } else if (annotationType === AnnotationTypeEnum.CheckBox) {
    const checkBoxWidget = new PSPDFKit.Annotations.WidgetAnnotation({
      id: instantId,
      name: instantId,
      pageIndex: pageIndex,
      formFieldName: instantId,
      boundingBox: pageRect,
      customData: {
        createdBy: user.id,
        type: annotationType,
      },
    });
    const formField = new PSPDFKit.FormFields.CheckBoxFormField({
      id: instantId,
      name: instantId,
      annotationIds: PSPDFKit.Immutable.List.of(checkBoxWidget.id),
      defaultValue: false,
      options: PSPDFKit.Immutable.List.of(
        new PSPDFKit.FormOption({
          label: 'Option 1',
          value: '1',
        }),
      ),
    });
    await inst.create([checkBoxWidget, formField]);
  } else if (annotationType === AnnotationTypeEnum.TextField) {
    const textBoxWidget = new PSPDFKit.Annotations.WidgetAnnotation({
      id: instantId,
      pageIndex: pageIndex,
      name: instantId,
      formFieldName: instantId,
      boundingBox: pageRect,
      customData: {
        createdBy: user.id,
        type: annotationType,
      },
    });
    const textField = new PSPDFKit.FormFields.TextFormField({
      annotationIds: PSPDFKit.Immutable.List.of(textBoxWidget.id),
      id: instantId,
      name: instantId,
      label: 'Text Field',
      maxLength: 100,
      multiLine: false,
    });
    await inst.create([textBoxWidget, textField]);
  } else if (annotationType === AnnotationTypeEnum.ComboBox) {
    const comboBoxWidget = new PSPDFKit.Annotations.WidgetAnnotation({
      id: instantId,
      pageIndex: pageIndex,
      name: instantId,
      formFieldName: instantId,
      boundingBox: pageRect,
      customData: {
        createdBy: user.id,
        type: annotationType,
      },
    });
    const comboBoxField = new PSPDFKit.FormFields.ComboBoxFormField({
      annotationIds: PSPDFKit.Immutable.List.of(comboBoxWidget.id),
      id: instantId,
      name: instantId,
      label: 'Combo Box',
      maxLength: 100,
      multiLine: false,
    });
    await inst.create([comboBoxWidget, comboBoxField]);
  } else if (annotationType === AnnotationTypeEnum.ListBox) {
    const listBoxWidget = new PSPDFKit.Annotations.WidgetAnnotation({
      id: instantId,
      pageIndex: pageIndex,
      name: instantId,
      formFieldName: instantId,
      boundingBox: pageRect,
      customData: {
        createdBy: user.id,
        type: annotationType,
      },
    });
    const listBoxField = new PSPDFKit.FormFields.ListBoxFormField({
      annotationIds: PSPDFKit.Immutable.List.of(listBoxWidget.id),
      id: instantId,
      name: instantId,
      label: 'List Box',
      maxLength: 100,
      multiLine: false,
    });
    await inst.create([listBoxWidget, listBoxField]);
  } else if (annotationType === AnnotationTypeEnum.DateAndTimeField) {
    // Extract format from drag data - format will be passed by form creator
    // dataArray format: [name, email, InstantId, annotationType, liveFormType, isPlaceholder, dateFormat]
    const selectedDateFormat = dataArray[6] || 'US'; // Default to US if no format specified
    const dateFormat = getDateFormat(selectedDateFormat);

    const dateTimeWidget = new PSPDFKit.Annotations.WidgetAnnotation({
      id: instantId,
      pageIndex: pageIndex,
      name: instantId,
      formFieldName: instantId,
      boundingBox: pageRect,
      richText: false,
      backgroundColor: PSPDFKit.Color.WHITE,
      borderColor: PSPDFKit.Color.BLACK,
      borderWidth: 1,
      additionalActions: {
        onFormat: new PSPDFKit.Actions.JavaScriptAction({
          script: `AFDate_FormatEx("${dateFormat.script}")`,
        }),
      },
      customData: {
        createdBy: user.id,
        type: annotationType,
        dateFormat: dateFormat.script,
        dateFormatDisplay: dateFormat.display,
      },
    });

    const dateTimeField = new PSPDFKit.FormFields.TextFormField({
      annotationIds: PSPDFKit.Immutable.List.of(dateTimeWidget.id),
      id: instantId,
      name: instantId,
      label: `Date Field (${dateFormat.display})`,
      maxLength: dateFormat.maxLength,
      multiLine: false,
      readOnly: false,
      richText: false,
      defaultValue: dateFormat.placeholder,
      additionalActions: {
        onFormat: new PSPDFKit.Actions.JavaScriptAction({
          script: `AFDate_FormatEx("${dateFormat.script}")`,
        }),
        onInput: new PSPDFKit.Actions.JavaScriptAction({
          script: `
            // Use the fixed format specified by form creator
            var formatScript = "${dateFormat.script}";
            
            if (event.change && event.change !== "") {
              var testValue = event.value + event.change;
              if (testValue !== "${dateFormat.placeholder}") {
                var isValid = AFDate_Keystroke(formatScript);
                if (!isValid) {
                  event.rc = false;
                }
              }
            }
          `,
        }),
        onChange: new PSPDFKit.Actions.JavaScriptAction({
          script: `
            // Validate using the format specified by form creator
            var formatScript = "${dateFormat.script}";
            var formatDisplay = "${dateFormat.display}";
            
            if (event.value !== "" && event.value !== "${dateFormat.placeholder}") {
              var isValid = AFDate_Keystroke(formatScript);
              if (!isValid) {
                app.alert("Please enter a valid date in " + formatDisplay + " format");
                event.rc = false;
              }
            }
          `,
        }),
      },
    });

    await inst.create([dateTimeWidget, dateTimeField]);
  } else if (annotationType === AnnotationTypeEnum.SIGNATURE_WITH_DATE_SIGNED) {
    // Create separate IDs for signature and date fields
    const signatureId = instantId;
    const dateId = PSPDFKit.generateInstantId();
    const selectedDateFormat = dataArray[6] || 'US'; // Default to US if no format specified
    const dateFormat = getDateFormat(selectedDateFormat);

    let signatureCustomData: Record<string, unknown> = {
      createdBy: user.id,
      type: annotationType,
      isPlaceholder: Boolean(Number(isPlaceholder)),
      relatedDateFieldId: dateId,
    };

    if (liveFormType === 'multi') {
      signatureCustomData = {
        ...signatureCustomData,
        signerID: signee.id,
        name: signee.first_name + ' ' + signee.last_name,
      };
    }

    // Create signature widget
    const signatureWidget = new PSPDFKit.Annotations.WidgetAnnotation({
      id: signatureId,
      pageIndex: pageIndex,
      name: signatureId,
      formFieldName: signatureId,
      boundingBox: pageRect,
      subject: liveFormType,
      customData: signatureCustomData,
    });

    // Create date widget positioned below the signature
    const dateWidgetRect = new PSPDFKit.Geometry.Rect({
      left: pageRect.left,
      top: pageRect.bottom + 5, // 5 points below signature
      width: pageRect.width,
      height: 35,
    });

    let dateCustomData: Record<string, unknown> = {
      createdBy: user.id,
      type: AnnotationTypeEnum.DATE_SIGNED,
      relatedSignatureFieldId: signatureId,
      isPlaceholder: Boolean(Number(isPlaceholder)),
    };

    if (liveFormType === 'multi') {
      dateCustomData = {
        ...dateCustomData,
        signerID: signee.id,
        name: signee.first_name + ' ' + signee.last_name,
      };
    }

    const dateWidget = new PSPDFKit.Annotations.WidgetAnnotation({
      id: dateId,
      pageIndex: pageIndex,
      name: dateId,
      formFieldName: dateId,
      boundingBox: dateWidgetRect,
      backgroundColor: PSPDFKit.Color.WHITE,
      borderColor: PSPDFKit.Color.LIGHT_GREY,
      borderWidth: 1,
      customData: dateCustomData,
    });

    // Create signature form field
    const signatureFormField = new PSPDFKit.FormFields.SignatureFormField({
      annotationIds: PSPDFKit.Immutable.List([signatureId]),
      name: signatureId,
      id: signatureId,
      readOnly: signee.id !== user.id,
      defaultValue: name,
    });

    const dateFormField = new PSPDFKit.FormFields.TextFormField({
      annotationIds: PSPDFKit.Immutable.List([dateId]),
      id: dateId,
      name: dateId,
      label: `Date Signed (${dateFormat.display})`,
      maxLength: 10,
      multiLine: false,
      readOnly: true,
      defaultValue: dateFormat.placeholder,
    });

    await inst.create([
      signatureWidget,
      dateWidget,
      signatureFormField,
      dateFormField,
    ]);
  } else {
    const text = new PSPDFKit.Annotations.TextAnnotation({
      pageIndex,
      boundingBox: pageRect,
      text: {
        format: 'plain',
        value: annotationType === 'name' ? name : new Date().toDateString(),
      },
      name: name,
      customData: {
        createdBy: user.id,
        type: annotationType,
      },
      font: 'Helvetica',
      fontSize: rectHeight * 0.6,
      horizontalAlign: 'left',
      verticalAlign: 'top',
      isEditable: false,
    });
    await inst.create(text);
  }
};

export default handleDrop;
